import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Spin, Tag } from 'antd';
import {
  ArrowRightOutlined,
  AimOutlined,
  ApartmentOutlined,
  BarChartOutlined,
  FundOutlined,
  PercentageOutlined,
  PieChartOutlined,
  TrophyOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { ORDER_STATUS_COLORS } from '../../../utils/orderStatus';
import {
  Bars,
  Callout,
  DashCard,
  Donut,
  KpiTile,
  MetricRows,
  money,
  percent,
  shortMoney,
} from './DashboardCharts';
import { fixedCostsByCategory, fixedCostsToDate, foldDashboardSlices, groupVariance, marginPercentFor, sumLine } from './dashboardModel';
import {
  displayOrderNumber,
  forecastMonthsByOrder,
  orderForecastMonthsText,
  orderMarginPercent,
  orderPortfolio,
  portfolioByGroup,
  topOpenOrders,
} from './ordersModel';
import { dk, type DashboardViewContext } from './viewContext';
import { CostPartialMark } from '../ReportTab';

// "Sprzedaż i zamówienia" and "Koszty i rentowność" views ("Płynność" is
// modules/reports/liquidity/LiquiditySection.tsx since 2026-10-29).


function Loading() {
  return <div className="mgmt-loading"><Spin /></div>;
}

export const OrdersView: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, language, orders, statement } = ctx;
  if (ctx.ordersFailed) return <Callout>{dk(t, 'errors.orders')}</Callout>;
  if (!orders) return <Loading />;
  // One set for the figures: the open orders and those closed by an invoice
  // as they are today (owner 2026-10-30; the period's forecast is the
  // „Prognoza” figure above, a different set: only the part still waiting for
  // its invoice in the period).
  const portfolio = orderPortfolio(orders);
  const groups = portfolioByGroup(orders).filter((group) => group.count > 0);
  const top = topOpenOrders(orders, 5);
  const monthsByOrder = forecastMonthsByOrder(statement);
  const orderLabel = (number: string) => dk(t, 'orderLabel', { number });
  return (
    <>
      {/* The portfolio is the state of today (not the period): a grouped list,
          not a second row of figure tiles (PR2 review). */}
      <div className="mgmt-grid mgmt-grid--half">
        <DashCard title={dk(t, 'orders.portfolioTitle')} icon={<WalletOutlined />} tone="indigo" lead={dk(t, 'orders.portfolioLead')} testId="mgmt-portfolio">
          <dl className="obj-facts mgmt-facts">
            {[
              { key: 'open', label: dk(t, 'orders.open'), title: dk(t, 'orders.openSub'), value: dk(t, 'countAmount', { count: portfolio.open.count, amount: money(portfolio.open.revenue) }) },
              { key: 'invoiced', label: dk(t, 'orders.invoiced'), title: dk(t, 'orders.invoicedSub'), value: dk(t, 'countAmount', { count: portfolio.invoiced.count, amount: money(portfolio.invoiced.revenue) }) },
              { key: 'portfolio', label: dk(t, 'orders.portfolio'), value: money(portfolio.revenue), title: dk(t, 'orders.portfolioHint') },
              {
                key: 'avg-margin',
                label: dk(t, 'orders.avgMargin'),
                title: portfolio.pendingCostCount > 0 ? dk(t, 'orders.avgMarginSubPartial', { count: portfolio.pendingCostCount }) : dk(t, 'orders.avgMarginSub'),
                value: (
                  <>
                    {percent(portfolio.marginPercent, language)}
                    {portfolio.pendingCostCount > 0 && <CostPartialMark hint={t('reports:profitability.percentPartial', { count: portfolio.pendingCostCount }) as string} testId="mgmt-kpi-partial" />}
                  </>
                ),
              },
              {
                key: 'to-invoice',
                label: dk(t, 'orders.toInvoice'),
                title: dk(t, 'overview.toInvoiceHint'),
                value: dk(t, 'countAmount', { count: statement.awaitingSettlementSummary.orderCount, amount: money(statement.awaitingSettlementSummary.revenue) }),
              },
            ].map((fact) => (
              <div className="obj-facts__row" key={fact.key} data-testid={`mgmt-fact-${fact.key}`}>
                <dt title={fact.title ?? fact.label}>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        </DashCard>
        <DashCard title={dk(t, 'orders.byStatus')} icon={<PieChartOutlined />} tone="blue" lead={dk(t, 'orders.byStatusLead')}>
          <Donut
            language={language}
            center={String(groups.reduce((sum, group) => sum + group.count, 0))}
            centerCaption={dk(t, 'orders.byStatusCaption')}
            emptyText={dk(t, 'orders.noOrders')}
            items={groups.map((group) => ({
              key: group.key,
              label: dk(t, `orders.groups.${group.key}`),
              value: group.count,
              display: dk(t, 'orders.countValue', { count: group.count, amount: money(group.revenue) }),
            }))}
          />
        </DashCard>
      </div>
      {ctx.aside}
      <DashCard title={dk(t, 'orders.profitability')} icon={<TrophyOutlined />} tone="green" lead={dk(t, 'orders.note')}>
        {top.length === 0 ? <p className="mgmt-empty">{dk(t, 'orders.noOpen')}</p> : (
          <div className="mgmt-table-wrap">
            <table className="mgmt-table mgmt-table--fixed">
              <colgroup>
                <col className="mgmt-col-order" />
                <col />
                <col className="mgmt-col-status" />
                <col className="mgmt-col-money" />
                <col className="mgmt-col-money" />
                <col className="mgmt-col-percent" />
                <col className="mgmt-col-month" />
              </colgroup>
              <thead>
                <tr>
                  <th>{dk(t, 'table.order')}</th>
                  <th>{dk(t, 'table.customer')}</th>
                  <th>{dk(t, 'table.status')}</th>
                  <th className="is-num">{dk(t, 'table.netValue')}</th>
                  <th className="is-num">{dk(t, 'table.cost')}</th>
                  <th className="is-num">{dk(t, 'table.marginPercent')}</th>
                  <th>{dk(t, 'table.forecastMonth')}</th>
                </tr>
              </thead>
              <tbody>
                {top.map((order) => {
                  const marginPct = orderMarginPercent(order);
                  const status = t(`orders:statusOptions.${order.status}`, { defaultValue: t('common:unknownStatus') }) as string;
                  return (
                    <tr key={order.id}>
                      <td><Link className="report-cut" to={`/app/orders/${order.id}`} title={orderLabel(displayOrderNumber(order))}>{orderLabel(displayOrderNumber(order))}</Link></td>
                      <td><span className="report-cut" title={order.customer?.name ?? '—'}>{order.customer?.name ?? '—'}</span></td>
                      <td>
                        <Tag className="mgmt-tag" color={ORDER_STATUS_COLORS[order.status] ?? 'default'} title={status}>{status}</Tag>
                      </td>
                      <td className="is-num">{money(order.revenue)}</td>
                      <td className="is-num">{(order.totalCost ?? 0) > 0 ? money(order.totalCost) : dk(t, 'orders.noCost')}</td>
                      <td className={`is-num ${marginPct != null && marginPct < 0 ? 'is-negative' : ''}`}>{marginPct == null ? '' : percent(marginPct, language)}</td>
                      <td data-testid="mgmt-order-forecast-months"><span className="report-cut">{orderForecastMonthsText(order, monthsByOrder, language)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DashCard>
    </>
  );
};

export const CostsView: React.FC<{ ctx: DashboardViewContext }> = ({ ctx }) => {
  const { t, language, periodMonths } = ctx;
  const elapsed = periodMonths.filter((month) => month.elapsed);
  const revenue = sumLine(elapsed, 'actual', (line) => line.revenue).value ?? 0;
  const goods = sumLine(elapsed, 'actual', (line) => line.orderCosts, true);
  const margin = sumLine(elapsed, 'actual', (line) => line.margin, true);
  const fixed = sumLine(elapsed, 'actual', (line) => line.operatingFixedCosts.total).value ?? 0;
  const ebitda = sumLine(elapsed, 'actual', (line) => line.ebitda, true);
  const depreciation = sumLine(elapsed, 'actual', (line) => line.depreciation).value ?? 0;
  const financial = sumLine(elapsed, 'actual', (line) => line.financialCosts).value ?? 0;
  const preTax = sumLine(elapsed, 'actual', (line) => line.preTaxResult, true);
  const tax = sumLine(elapsed, 'actual', (line) => line.incomeTax, true);
  const profit = sumLine(elapsed, 'actual', (line) => line.profit, true);
  const categoryLabel = (code: string) => t(`fixedCosts:categories.${code}`, { defaultValue: code }) as string;
  const actualByCategory = fixedCostsByCategory(elapsed, 'actual');
  const slices = foldDashboardSlices([
    { key: 'goods', label: dk(t, 'costs.goods'), value: goods.value ?? 0 },
    ...Object.entries(actualByCategory).map(([code, value]) => ({ key: code, label: categoryLabel(code), value })),
    { key: 'depreciation', label: dk(t, 'costs.depreciation'), value: depreciation },
    { key: 'financial', label: dk(t, 'costs.financial'), value: financial },
  ], 7, dk(t, 'costs.other'));
  const groups = ctx.budget?.groups ?? [];
  const groupRows = groupVariance(periodMonths, groups);
  const groupTotal = groupRows.reduce((sum, row) => ({
    plan: row.plan == null ? sum.plan : (sum.plan ?? 0) + row.plan,
    actual: sum.actual + row.actual,
    forecast: sum.forecast + row.forecast,
  }), { plan: null as number | null, actual: 0, forecast: 0 });
  const groupLabel = (row: (typeof groupRows)[number]) => {
    if (!row.group) return row.key === 'none' ? dk(t, 'groups.none') : dk(t, 'groups.unknown');
    return row.group.isActive ? row.group.name : dk(t, 'groups.archived', { name: row.group.name });
  };
  const toDate = fixedCostsToDate(periodMonths);
  const signed = (value: number | null) => (value == null ? '—' : value > 0 ? `+ ${money(value)}` : value < 0 ? `− ${money(Math.abs(value))}` : money(0));
  const incomplete = goods.incomplete || profit.incomplete;
  const marginPct = marginPercentFor(elapsed, 'actual');
  const partialHint = dk(t, 'notes.incompleteProfitHint');
  // The known part of an incomplete cost, marked (decision 2026-10-29).
  const marked = (text: string, sum: { incomplete: boolean; value: number | null }) => (
    sum.incomplete && sum.value != null ? <>{text}<CostPartialMark hint={partialHint} testId="mgmt-bridge-partial" /></> : text
  );
  return (
    <>
      <div className="report-figures mgmt-figures">
        <KpiTile icon={<WalletOutlined />} iconTone="orange" label={dk(t, 'costs.operating')} value={money(fixed)} sub={dk(t, 'costs.operatingSub')} />
        <KpiTile icon={<PercentageOutlined />} iconTone="indigo" label={dk(t, 'costs.marginPct')} value={percent(marginPct.value, language)} sub={dk(t, 'costs.marginPctSub')} marker={marginPct.partial || margin.incomplete} markerHint={partialHint} />
        <KpiTile icon={<BarChartOutlined />} iconTone="teal" label={dk(t, 'costs.ebitda')} value={money(ebitda.value)} sub={dk(t, 'costs.ebitdaSub')} marker={ebitda.incomplete} markerHint={partialHint} tone={(ebitda.value ?? 0) < 0 ? 'negative' : undefined} />
        <KpiTile icon={<FundOutlined />} iconTone="green" label={dk(t, 'costs.netProfit')} value={money(profit.value)} sub={dk(t, 'costs.netProfitSub')} marker={profit.incomplete} markerHint={partialHint} tone={(profit.value ?? 0) < 0 ? 'negative' : undefined} />
      </div>
      {incomplete && <Callout title={partialHint}>{dk(t, 'notes.incompleteProfit')}</Callout>}
      <div className="mgmt-grid mgmt-grid--half">
        <DashCard title={dk(t, 'costs.structure', { period: ctx.periodLabel })} icon={<PieChartOutlined />} tone="orange">
          <Donut
            language={language}
            center={shortMoney(slices.reduce((sum, slice) => sum + slice.value, 0), language)}
            centerCaption={dk(t, 'overview.costStructureCaption')}
            emptyText={dk(t, 'empty')}
            items={slices}
          />
        </DashCard>
        <DashCard title={dk(t, 'costs.byCategory')} icon={<BarChartOutlined />} tone="blue">
          <Bars emptyText={dk(t, 'empty')} items={slices} />
        </DashCard>
      </div>
      {/* Plan vs actual of the groups is the wider card (four amounts), the bridge beside it. */}
      <div className="mgmt-grid mgmt-grid--groups">
        <DashCard
          title={dk(t, 'groups.title')}
          icon={<ApartmentOutlined />}
          tone="blue"
          lead={dk(t, 'groups.lead', { period: ctx.periodLabel })}
          testId="mgmt-cost-groups"
          extra={ctx.canEditTargets ? (
            <Button size="small" icon={<AimOutlined />} onClick={ctx.onEditTargets} data-testid="mgmt-groups-plan">{dk(t, 'groups.editPlan')}</Button>
          ) : undefined}
        >
          {ctx.budgetFailed ? <Callout>{dk(t, 'targets.unavailable')}</Callout> : (
            <div className="mgmt-table-wrap">
              <table className="mgmt-table mgmt-table--fixed mgmt-groups-table">
                <colgroup>
                  <col />
                  <col className="mgmt-col-amount" />
                  <col className="mgmt-col-amount" />
                  <col className="mgmt-col-amount" />
                  <col className="mgmt-col-diff" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{dk(t, 'groups.group')}</th>
                    <th className="is-num">{dk(t, 'groups.plan')}</th>
                    <th className="is-num">{dk(t, 'groups.actual')}</th>
                    <th className="is-num">{dk(t, 'groups.forecast')}</th>
                    <th className="is-num" title={dk(t, 'groups.differenceHint')}>{dk(t, 'groups.difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => (
                    <tr key={row.key} data-testid={`mgmt-group-row-${row.key}`}>
                      <td><span className={`report-cut ${row.group ? '' : 'report-muted'}`} title={groupLabel(row)}>{groupLabel(row)}</span></td>
                      <td className="is-num">{row.plan == null ? '—' : money(row.plan)}</td>
                      <td className="is-num">{money(row.actual)}</td>
                      <td className="is-num">{money(row.forecast)}</td>
                      <td className={`is-num ${row.difference != null && row.difference > 0 ? 'is-negative' : ''}`}>{signed(row.difference)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr data-testid="mgmt-group-total">
                    <td title={dk(t, 'table.total', { period: ctx.periodLabel })}>{dk(t, 'groups.total')}</td>
                    <td className="is-num">{groupTotal.plan == null ? '—' : money(groupTotal.plan)}</td>
                    <td className="is-num">{money(groupTotal.actual)}</td>
                    <td className="is-num">{money(groupTotal.forecast)}</td>
                    <td className={`is-num ${groupTotal.plan != null && groupTotal.forecast - groupTotal.plan > 0 ? 'is-negative' : ''}`}>{signed(groupTotal.plan == null ? null : groupTotal.forecast - groupTotal.plan)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {!ctx.budgetFailed && toDate.months > 0 && (
            // Plan and realization of the months that already happened: the
            // fair comparison next to the period plan above (PR3 review).
            <MetricRows rows={[
              { label: dk(t, 'groups.toDatePlan', { count: toDate.months }), value: money(toDate.plan) },
              { label: dk(t, 'groups.toDateActual'), value: money(toDate.actual) },
              {
                label: dk(t, 'groups.toDateDifference'),
                value: signed(toDate.plan == null ? null : toDate.actual - toDate.plan),
                tone: toDate.plan != null && toDate.actual > toDate.plan ? 'negative' : undefined,
              },
            ]}
            />
          )}
          {!ctx.budgetFailed && groups.length === 0 && <Callout title={dk(t, 'groups.noGroupsHint')}>{dk(t, 'groups.noGroups')}</Callout>}
        </DashCard>
        <DashCard
          title={dk(t, 'costs.bridge')}
          icon={<FundOutlined />}
          tone="green"
          lead={dk(t, 'costs.bridgeNote')}
          extra={(
            <Button size="small" type="link" onClick={() => ctx.openTab('income-statement')} title={dk(t, 'costs.fullStatement')} aria-label={dk(t, 'costs.fullStatement')}>
              <span className="mgmt-link-text">{dk(t, 'costs.fullStatement')}</span> <ArrowRightOutlined />
            </Button>
          )}
        >
          <MetricRows rows={[
            { label: dk(t, 'bridge.revenue'), value: money(revenue) },
            { label: dk(t, 'bridge.goods'), value: marked(`− ${money(goods.value)}`, goods) },
            { label: dk(t, 'bridge.margin'), value: marked(money(margin.value), margin), tone: 'strong' },
            { label: dk(t, 'bridge.fixed'), value: `− ${money(fixed)}` },
            { label: dk(t, 'bridge.ebitda'), value: marked(money(ebitda.value), ebitda), tone: 'strong' },
            { label: dk(t, 'bridge.depreciation'), value: `− ${money(depreciation)}` },
            { label: dk(t, 'bridge.financial'), value: `− ${money(financial)}` },
            { label: dk(t, 'bridge.preTax'), value: marked(money(preTax.value), preTax) },
            { label: dk(t, 'bridge.tax'), value: `− ${money(tax.value)}` },
            { label: dk(t, 'bridge.profit'), value: marked(money(profit.value), profit), tone: (profit.value ?? 0) < 0 ? 'negative' : 'positive' },
          ]}
          />
        </DashCard>
      </div>
    </>
  );
};
